import { useMemo, useState, useId } from "react";
import { SaveEditFormResponse, EditFormElement, EditForm } from "@kaltura/mediaspace-shared-edit-entry";
import { MediaUploadFileInfo } from "../../upload/UploadMediaProps";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import RowActions from "./row-actions/RowActions";
import Thumbnail from "./thumbnail/Thumbnail";
import NameAndStatus from "./name-and-status/NameAndStatus";
import { StyledFileRow, StyledEditFormWrapper } from "./StyleComponents";
import { Collapse } from "@mui/material";

export interface FileRowProps {
    className?: string;
    /**
     * info to show
     */
    fileInfo: MediaUploadFileInfo;

    /**
     * is edit panel currently open
     */
    isOpen: boolean;

    /**
     * should edit panel close
     */
    shouldClose: boolean;

    /**
     * list of form elements to show on edit form
     */
    editForm: EditFormElement[];

    /**
     * list of components to show on edit form
     */
    editFormComponents?: KmsTypeComponent[];
    /**
     * callback for cancel file upload button
     * @param uploadTokenId
     */
    onCancel: (uploadTokenId: string) => void;

    /**
     * callback for edit button
     * @param uploadTokenId
     */
    onEdit: (uploadTokenId: string) => void;

    /**
     * callback when the form is dirty and can be saved
     */
    onCanSaveForm?: (onCanSaveForm: boolean) => void;

    /**
     * callback when the entries are ready for publish
     */
    onReadyForPublish?: (entryIds: string[], readyForPublish: boolean) => void;

    /**
     * callback for submit started
     */
    onSubmitStarted?: () => void;

    /**
     * callback for submit done
     */
    onSubmit: (data: SaveEditFormResponse, isEditable?: boolean) => void;

    /**
     * callback for "remove from list" button
     * @param uploadTokenId
     */
    onRemove: (uploadTokenId: string) => void;

    /**
     * callback for closing edit form
     */
    onClose: () => void;

    /**
     * used to remount each single entry publish action menu
     * to refresh its data after bulk updates
     */
    bulkUpdatesCount?: number;

    /**
     * boolean flag to indicate publish is enabled
     */
    publishEnabled?: boolean;

    /**
     * string to connect an external submit button
     */
    formId?: string;
    /**
     * should we allow single or multiple uploads
     */
    singleUpload?: boolean;
    /**
     * show edit form cancel/save buttons
     */
    showEditFormButtons?: boolean;
    /**
     * show cancel button
     */
    showCancelButton?: boolean;
    /**
     * show the media link
     */
    showMediaLink?: boolean;
    /**
     * custom label for the "save" button
     */
    saveButtonLabel?: string;
    /**
     * allow saving the form even if it was not edited
     */
    allowSaveUntouched?: boolean;
    /**
     * callback for before submit
     */
    onBeforeSubmit?: (entryIds: string[], data: any) => Promise<{ confirm: boolean; isEditable: boolean }>;
}

const FileRow = (props: FileRowProps) => {
    const {
        fileInfo,
        isOpen,
        shouldClose,
        editForm,
        editFormComponents,
        onSubmitStarted,
        onBeforeSubmit,
        onSubmit,
        onClose,
        bulkUpdatesCount = 0,
        formId,
        singleUpload,
        showEditFormButtons,
        ...rest
    } = props;
    // used to set the single entry publish action idle
    // publish props are refreshed asynchronously after a successful edit
    // so publish is idle while waiting for edit submit response
    const [isEditProcessing, setIsEditProcessing] = useState(false);
    // used to remount the single entry publish action after
    // entry edit finished - to refresh the publish menu props by it
    const [editCount, setEditCount] = useState(0);

    const generatedId = useId();
    const rowId = fileInfo.entry?.id || generatedId; // Used for DOM element IDs
    const entryId = fileInfo.entry?.id || "";
    const entryName = fileInfo.entry?.name || fileInfo.file?.name || "";
    const isEditable = fileInfo.isEditable ?? true;

    const actions = useMemo(() => {
        if (!isEditable) {
            return;
        }

        return (
            <RowActions
                {...rest}
                entryId={entryId}
                rowId={rowId}
                tokenId={fileInfo.fileMetaData.tokenId}
                status={fileInfo.fileMetaData.status}
                entryName={fileInfo.entry?.name}
                isOpen={isOpen || !!singleUpload}
                showEditButton={!singleUpload}
                showRemoveButton={!singleUpload}
                isEditProcessing={isEditProcessing}
                publishRefreshKey={bulkUpdatesCount + editCount}
            />
        );
    }, [
        fileInfo.entry?.id,
        fileInfo.fileMetaData.status,
        fileInfo.fileMetaData.tokenId,
        isEditable,
        rest,
        isOpen,
        isEditProcessing,
        bulkUpdatesCount,
        editCount,
        fileInfo.entry?.name,
        singleUpload,
        rowId,
    ]);

    const formData = useMemo(
        () =>
            Object.assign(
                {
                    name: entryName,
                    description: fileInfo.entry?.description || "",
                    tags: fileInfo.entry?.tags || "",
                },
                fileInfo.entryMetadata || {}
            ),
        [entryName, fileInfo.entry?.description, fileInfo.entry?.tags, fileInfo.entryMetadata]
    );

    const handleSubmitStarted = () => {
        setIsEditProcessing(true);
        onSubmitStarted?.();
    };

    const handleSubmit = (data: SaveEditFormResponse, isEditable = true) => {
        onSubmit?.(data, isEditable);
        setIsEditProcessing(false);
        setEditCount((prev) => prev + 1);
    };

    const collapseProps = singleUpload ? { timeout: "auto" as const, unmountOnExit: true } : {};

    return (
        <div className={props.className}>
            <div id={`${rowId}-row`}>
                <StyledFileRow isOpen={isOpen} className={"kms-ds-upload-media-file-row"}>
                    <Thumbnail file={fileInfo} />
                    <NameAndStatus name={entryName} metadata={fileInfo.fileMetaData} />
                    {actions}
                </StyledFileRow>
            </div>
            <Collapse in={isOpen} id={`${rowId}-edit`} {...collapseProps}>
                <StyledEditFormWrapper singleUpload={singleUpload}>
                    <EditForm
                        entryIds={[entryId]}
                        onCanSaveForm={props.onCanSaveForm}
                        onReadyForPublish={props.onReadyForPublish}
                        onSubmitStarted={handleSubmitStarted}
                        onSubmit={handleSubmit}
                        formData={formData}
                        fieldsStructure={editForm}
                        components={editFormComponents}
                        shouldClose={shouldClose}
                        onClose={onClose}
                        formId={formId}
                        showEditFormButtons={showEditFormButtons && isEditable}
                        showCancelButton={props.showCancelButton}
                        saveButtonLabel={props.saveButtonLabel}
                        allowSaveUntouched={props.allowSaveUntouched}
                        onBeforeSubmit={onBeforeSubmit}
                    />
                </StyledEditFormWrapper>
            </Collapse>
        </div>
    );
};

export default FileRow;
